import React, { useRef, useState } from "react";
import { UploadSimple, File as FileIcon, CircleNotch, X, CheckCircle } from "@phosphor-icons/react";
import { uploadFile } from "@/lib/api";
import { toast } from "sonner";

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx";

export const FileUpload = ({ label, hint, category, value = [], onChange, testid }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList);
    if (!files.length) return;
    setBusy(true);
    const uploaded = [];
    for (const f of files) {
      if (f.size > 25 * 1024 * 1024) {
        toast.error(`${f.name} exceeds the 25MB limit.`);
        continue;
      }
      try {
        const res = await uploadFile(f, category);
        uploaded.push(res);
      } catch (e) {
        toast.error(`Could not upload ${f.name}. Please try again.`);
      }
    }
    if (uploaded.length) {
      onChange([...(value || []), ...uploaded]);
      toast.success(`${uploaded.length} file${uploaded.length > 1 ? "s" : ""} uploaded.`);
    }
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = (id) => onChange(value.filter((f) => f.id !== id));

  return (
    <div data-testid={testid}>
      {label && (
        <label className="block text-sm font-semibold text-forest mb-2 uppercase tracking-wider">{label}</label>
      )}
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); handleFiles(e.dataTransfer.files); }}
        className={`cursor-pointer rounded-sm border border-dashed p-8 text-center transition-colors duration-200 ${
          drag ? "border-gold bg-gold/5" : "border-line bg-white hover:border-gold"
        }`}
        data-testid={`${testid}-dropzone`}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
        <div className="flex flex-col items-center gap-2 text-ink">
          {busy ? (
            <CircleNotch size={30} className="text-gold animate-spin" />
          ) : (
            <UploadSimple size={30} weight="light" className="text-gold" />
          )}
          <p className="text-sm font-medium text-forest">
            {busy ? "Uploading…" : "Click to upload or drag & drop"}
          </p>
          <p className="text-xs text-ink/70">{hint || "PDF, JPG, PNG, DOC — up to 25MB each"}</p>
        </div>
      </div>

      {value?.length > 0 && (
        <ul className="mt-3 space-y-2">
          {value.map((f) => (
            <li key={f.id} className="flex items-center justify-between gap-3 bg-white border border-line rounded-sm px-4 py-2.5 text-sm">
              <span className="flex items-center gap-2 min-w-0 text-forest">
                <CheckCircle size={18} weight="fill" className="text-gold shrink-0" />
                <FileIcon size={16} className="shrink-0 text-ink" />
                <span className="truncate">{f.original_filename}</span>
              </span>
              <button
                type="button"
                onClick={() => remove(f.id)}
                className="text-ink hover:text-destructive transition-colors shrink-0"
                aria-label="Remove file"
              >
                <X size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default FileUpload;
